import { z } from 'zod';
/**
 * What a connector's provider charges, as data the operator keeps up to date.
 *
 * Accounting metadata, not part of the HTTP contract: changing it never resets
 * a connector's verification. The platform multiplies what a job consumed by
 * these prices to estimate its cost; it never invents a price, so a connector
 * with no pricing yields "not priced", not zero.
 */
/** What one price is for. */
export const GENERATION_PRICING_UNITS = ['SECOND', 'IMAGE', 'CHARS_10K', 'TOKENS_1M', 'REQUEST'];
const price = z.number().finite().min(0).max(100_000);
export const generationPricingSchema = z.object({
    unit: z.enum(GENERATION_PRICING_UNITS),
    /** Per second / image / 10,000 characters / request. */
    price: price.optional(),
    /** TOKENS_1M only: per million input (prompt) tokens. */
    inputPrice: price.optional(),
    /** TOKENS_1M only: per million output (reply) tokens. */
    outputPrice: price.optional(),
    /** SECOND only: a price per resolution (`480P`, `720P`, `1080P`), used when the job chose one. */
    byResolution: z.record(z.string().trim().min(1).max(20), price).optional(),
    /** SECOND only: the per-second price when the video carries generated sound (Wan 2.6 flash: silent 0.025, with sound 0.05). */
    soundPrice: price.optional(),
    currency: z.literal('USD').default('USD'),
});
/** True when the pricing carries at least one number the unit can use. */
export function pricingHasPrice(pricing) {
    if (!pricing)
        return false;
    if (pricing.unit === 'TOKENS_1M')
        return pricing.inputPrice !== undefined || pricing.outputPrice !== undefined;
    return (pricing.price !== undefined ||
        (pricing.unit === 'SECOND' && Object.keys(pricing.byResolution ?? {}).length > 0));
}
/** Stored JSON read back safely: anything that does not fit is "no pricing". */
export function parseGenerationPricing(value) {
    if (value === null || value === undefined)
        return null;
    const parsed = generationPricingSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=generationPricing.js.map